import {
  CardIcon,
  ClusterIcon,
  EditIcon,
  LinkedIcon,
  PadlockIcon,
  PowerCordPlugIcon,
  RocketIcon,
  SquareNewWindowIcon,
  StarEmptyIcon,
  UsersIcon,
} from "@codat/orchard-ui";
import { IconName } from "react-cmdk";
import formatUrl from "utils/formatUrl";

import {
  NavPermissionMap,
  canViewNavGroupLink,
  getNavGroupFirstPagePathOrDefault,
} from "components/NavContainer/utils";

export type CommandSearchListItem = {
  id: string;
  label: string;
  icon: IconName | React.FC;
  keywords: string[];
  onClick: () => void;
};

export type CommandSearchListGroup = {
  id: string;
  label: string;
  items: CommandSearchListItem[];
};

const isDefined = (
  item: CommandSearchListItem | undefined
): item is CommandSearchListItem => {
  return !!item;
};

const createItem = (item: CommandSearchListItem) => item;

const ifHasPermission = (
  permission: boolean,
  item: CommandSearchListItem
): CommandSearchListItem | undefined => {
  return permission ? item : undefined;
};

export const getCommandSearchListItems = ({
  navPermissionMap,
  navigate,
}: {
  navPermissionMap: NavPermissionMap;
  navigate: (url: string) => void;
}): CommandSearchListGroup[] => {
  const makeNavigateFn = (path: string) => () => {
    navigate(path);
  };

  return [
    {
      id: "suggested",
      label: "SUGGESTED",
      items: [
        ifHasPermission(navPermissionMap.companies.permission, {
          id: "create-a-company",
          label: "Create a company",
          icon: "BuildingOfficeIcon",
          keywords: [
            "create a company",
            "create company",
            "add company",
            "add new company",
          ],
          onClick: () =>
            navigate(
              formatUrl(
                navPermissionMap.companies.path,
                {},
                {
                  newCompany: true,
                }
              )
            ),
        }),
      ].filter(isDefined),
    },
    {
      id: "pages",
      label: "PAGES",
      items: [
        createItem({
          id: "home",
          label: "Home",
          icon: "HomeIcon",
          keywords: ["home"],
          onClick: () => navigate(navPermissionMap.home.path),
        }),
        ifHasPermission(navPermissionMap.companies.permission, {
          id: "companies",
          label: "Companies",
          icon: "BuildingOfficeIcon",
          keywords: ["companies", "company"],
          onClick: () => navigate(navPermissionMap.companies.path),
        }),
        ifHasPermission(canViewNavGroupLink(navPermissionMap.monitor), {
          id: "monitor",
          label: "Monitor",
          icon: "PresentationChartLineIcon",
          keywords: [
            "monitor",
            "raised alerts",
            "alerting rules",
            "alert settings",
            "offline connectors",
            "on-premise connectors",
            "on premise connectors",
            "connector health",
          ],
          onClick: () =>
            navigate(
              getNavGroupFirstPagePathOrDefault(navPermissionMap.monitor)
            ),
        }),
        ifHasPermission(canViewNavGroupLink(navPermissionMap.developers), {
          id: "developers",
          label: "Developers",
          icon: "CodeBracketIcon",
          keywords: [
            "developers",
            "API keys",
            "auth headers",
            "API deprecations",
            "API status",
            "resources",
            "getting started guide",
            "API reference",
            "postman",
          ],
          onClick: () =>
            navigate(
              getNavGroupFirstPagePathOrDefault(navPermissionMap.developers)
            ),
        }),
      ].filter(isDefined),
    },
    {
      id: "settings",
      label: "SETTINGS",
      items: [
        ifHasPermission(navPermissionMap.settings.linkSettings.permission, {
          id: "link-settings",
          label: "Link settings",
          icon: LinkedIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.linkSettings.path),
          keywords: ["link settings"],
        }),
        ifHasPermission(navPermissionMap.settings.branding.permission, {
          id: "branding",
          label: "Branding",
          icon: StarEmptyIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.branding.path),
          keywords: ["branding"],
        }),
        ifHasPermission(navPermissionMap.settings.redirects.permission, {
          id: "redirects",
          label: "Redirects",
          icon: SquareNewWindowIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.redirects.path),
          keywords: ["redirects"],
        }),
        ifHasPermission(navPermissionMap.settings.integrations.permission, {
          id: "integrations",
          label: "Integrations",
          icon: PowerCordPlugIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.integrations.path),
          keywords: [
            "integrations",
            "accounting integrations",
            "banking integrations",
            "commerce integrations",
            "bank feed integrations",
            "other integrations",
          ],
        }),
        ifHasPermission(navPermissionMap.settings.dataTypes.permission, {
          id: "data-types",
          label: "Data types",
          icon: ClusterIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.dataTypes.path),
          keywords: ["datatypes", "data types"],
        }),
        ifHasPermission(navPermissionMap.settings.users.permission, {
          id: "users",
          label: "Users",
          icon: UsersIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.users.path),
          keywords: ["users", "manage users"],
        }),
        ifHasPermission(navPermissionMap.settings.billing.permission, {
          id: "billing",
          label: "Billing",
          icon: CardIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.billing.path),
          keywords: ["billing"],
        }),
        ifHasPermission(navPermissionMap.settings.products.permission, {
          id: "products",
          label: "Products",
          icon: RocketIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.products.path),
          keywords: ["products"],
        }),
        ifHasPermission(navPermissionMap.settings.webhookSecurity.permission, {
          id: "webhooks-settings-security",
          label: "Webhook security",
          icon: PadlockIcon,
          onClick: makeNavigateFn(
            navPermissionMap.settings.webhookSecurity.path
          ),
          keywords: ["webhooks", "web hooks"],
        }),
        ifHasPermission(navPermissionMap.settings.rules.permission, {
          id: "webhooks-settings-rules",
          label: "Webhook rules",
          icon: EditIcon,
          onClick: makeNavigateFn(navPermissionMap.settings.rules.path),
          keywords: ["webhooks", "web hooks"],
        }),
      ].filter(isDefined),
    },
    {
      id: "help",
      label: "HELP",
      items: [
        createItem({
          id: "codat-docs",
          label: "Codat docs",
          icon: "BookOpenIcon",
          onClick: () => {
            window.open("https://docs.codat.io");
          },
          keywords: ["codat docs", "documentation"],
        }),
        createItem({
          id: "help-center",
          label: "Help center",
          icon: "QuestionMarkCircleIcon",
          onClick: () => {
            window.open("https://codat.zendesk.com/");
          },
          keywords: ["help center", "help centre", "support"],
        }),
      ].filter(isDefined),
    },
  ].filter((x) => x.items.length);
};
